/**
 * Staff — Training Courses list + create.
 *
 * Simple list of courses (newest first). Each row shows the course name,
 * date, sections granted, and an N/M attendance summary. "Create" opens a
 * modal, "Open" links to the course-detail page.
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Plus, GraduationCap, Home, Calendar as CalendarIcon, Users, Mail as MailIcon, BellRing, MessageSquare, Upload, Trash2, Loader2, Pencil } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SmallBalancesCard } from '@/components/training/SmallBalancesCard';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { BRAND, accentForCourse } from '@/pages/trainingBrand';

const daysUntil = (iso) => {
  if (!iso) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(`${iso}T00:00:00`);
  return Math.round((d - today) / 86400000);
};

/** Red ≤ 21 days out (or past/undated), orange 22–25, green > 25; settled courses are always green. */
const collectUrgency = (c) => {
  const days = daysUntil(c.date);
  const owed = Number(c.balance_owed_total || 0) > 0;
  const daysText = days == null ? 'no course date' : days < 0 ? `${Math.abs(days)}d after course` : days === 0 ? 'course today' : `${days}d to course`;
  if (!owed) return { key: 'settled', days, daysText, box: 'bg-emerald-50 border-emerald-200', label: 'text-emerald-700', amount: 'text-emerald-700', title: () => 'Every booking on this course is paid in full' };
  const who = (c) => `${c.owing_count} trainee${c.owing_count === 1 ? '' : 's'} still to pay`;
  if (days == null || days <= 21) return { key: 'red', days, daysText, box: 'bg-rose-50 border-rose-300', label: 'text-rose-600', amount: 'text-rose-700', title: (c) => `${who(c)} — course is ${daysText} (within 21 days)` };
  if (days <= 25) return { key: 'orange', days, daysText, box: 'bg-orange-50 border-orange-300', label: 'text-orange-600', amount: 'text-orange-700', title: (c) => `${who(c)} — course is ${daysText}` };
  return { key: 'green', days, daysText, box: 'bg-emerald-50 border-emerald-300', label: 'text-emerald-700', amount: 'text-emerald-700', title: (c) => `${who(c)} — course is ${daysText}, plenty of time` };
};
import { formatApiError } from '@/utils/apiError';
import CourseSmsBlastModal from '@/components/training/CourseSmsBlastModal';
import ChangeCourseDateDialog from '@/components/training/ChangeCourseDateDialog';
import EditCourseDialog from '@/components/training/EditCourseDialog';
import SpreadsheetImportModal from '@/components/training/SpreadsheetImportModal';
import { SpacesLeftStepper } from '@/components/training/SpacesLeftStepper';

const GREEN = BRAND.green;

const SECTIONS = [
  { value: 'probound', label: 'ProBound' },
  { value: 'proepoxy', label: 'ProEpoxy' },
  { value: 'prodecor', label: 'ProDecor' },
];

const SECTION_LABEL = Object.fromEntries(SECTIONS.map(s => [s.value, s.label]));

const fmtCourseDate = (d) => {
  if (!d) return '';
  const dt = new Date(`${d}T00:00:00`);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

// Group courses by calendar month, soonest first. Courses already run go
// into one collapsed "Past courses" bucket at the bottom.
const monthLabel = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
const groupByMonth = (courses) => {
  const today = new Date().toISOString().slice(0, 10);
  const sorted = [...courses].sort((a, b) => (a.date || '9999').localeCompare(b.date || '9999') || (a.name || '').localeCompare(b.name || ''));
  const upcoming = {};
  const past = [];
  sorted.forEach((c) => {
    if (c.date && c.date < today) { past.push(c); return; }
    const key = c.date ? c.date.slice(0, 7) : 'tbc';
    (upcoming[key] ||= { key, label: c.date ? monthLabel(c.date) : 'Date to be confirmed', past: false, items: [] }).items.push(c);
  });
  const groups = Object.values(upcoming);
  if (past.length) groups.push({ key: 'past', label: 'Past courses', past: true, items: past.reverse() });
  return groups;
};

// Preset course names so staff can pick from a dropdown rather than typing
// the same three names over and over. Each preset auto-selects its section
// checkbox to speed things up further. "Custom" allows a bespoke title.
const COURSE_PRESETS = [
  { name: 'ProBound Training',   sections: ['probound'] },
  { name: 'ProEpoxy Training',   sections: ['proepoxy'] },
  { name: 'ProDecor Training',   sections: ['prodecor'] },
];

export default function TrainingCoursesList() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [creating, setCreating] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const groups = useMemo(() => groupByMonth(courses), [courses]);
  const [form, setForm] = useState({ name: '', date: '', sections: [], start_time: '' });
  const [preset, setPreset] = useState('');  // '' = nothing chosen yet, 'custom' = free-type
  const [busy, setBusy] = useState(false);
  const [chasing, setChasing] = useState(false);
  // The course currently being SMS-blasted from a row (opens the modal
  // in place — no navigation needed for a quick text-out).
  const [smsBlastCourse, setSmsBlastCourse] = useState(null);
  const [dateCourse, setDateCourse] = useState(null);
  const [editCourse, setEditCourse] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  // Course pending deletion. When set, opens the confirm dialog; on
  // confirm we fire DELETE and clear this. Kept as the full course row
  // so the dialog can show the name / date / trainee count.
  const [deletingCourse, setDeletingCourse] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const runChasesNow = async () => {
    setChasing(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/training-courses/chases/run`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Chase sweep failed');
      }
      const d = await res.json();
      const total = (d.sent_by_step?.soft || 0) + (d.sent_by_step?.firm || 0) + (d.sent_by_step?.final || 0);
      if (total === 0 && !d.errors) {
        toast.success('Nothing to chase — all trainees on upcoming courses are paid up or outside the 21-day window.');
      } else if (d.errors) {
        toast.warning(`Chased ${total} trainee(s); ${d.errors} email(s) failed — check Email diagnostics.`);
      } else {
        toast.success(`Chased ${total} trainee(s) · soft ${d.sent_by_step.soft} · firm ${d.sent_by_step.firm} · final ${d.sent_by_step.final}`);
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setChasing(false);
    }
  };

  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/training-courses`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load courses');
      setCourses(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    if (!user) return;
    if (!['admin', 'manager', 'team'].includes(user.role)) {
      navigate('/', { replace: true });
      return;
    }
    fetchCourses();
  }, [user, navigate, fetchCourses]);

  const toggleSection = (val) => {
    setForm(f => ({
      ...f,
      sections: f.sections.includes(val) ? f.sections.filter(s => s !== val) : [...f.sections, val],
    }));
  };

  const handlePresetChange = (value) => {
    setPreset(value);
    const p = COURSE_PRESETS.find(x => x.name === value);
    if (p) {
      // Pre-fill both the course name AND the section checkboxes.
      setForm(f => ({ ...f, name: p.name, sections: [...p.sections] }));
    } else if (value === 'custom') {
      setForm(f => ({ ...f, name: '' }));
    }
  };

  const submit = async () => {
    if (!form.name.trim() || !form.date || form.sections.length === 0) {
      toast.error('Give the course a name, date and at least one section.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/training-courses`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(formatApiError(e) || 'Failed to create course');
      }
      const course = await res.json();
      toast.success('Course created');
      setCreating(false);
      setForm({ name: '', date: '', sections: [], start_time: '' });
      setPreset('');
      navigate(`/staff/training-courses/${course.id}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${GREEN.tint} 0%, #FFFFFF 300px)` }}>
      <div className="border-b sticky top-0 z-20 shadow-sm" style={{ background: GREEN.primary, color: GREEN.ink }}>
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
          <Link to="/staff" className="text-white/80 hover:text-white shrink-0" data-testid="training-courses-back" aria-label="Back to Staff">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <Link to="/" className="text-white/80 hover:text-white shrink-0 hidden sm:inline-flex" aria-label="Home">
            <Home className="w-5 h-5" />
          </Link>
          <div className="w-8 h-8 rounded-lg bg-white/15 text-white flex items-center justify-center shrink-0">
            <GraduationCap className="w-4 h-4" />
          </div>
          <h1 className="text-base sm:text-lg font-bold text-white truncate flex-1 min-w-0">Training Courses</h1>
          <Link
            to="/staff/training-analytics"
            className="hidden md:inline text-xs text-white/80 hover:text-white underline shrink-0"
            data-testid="training-analytics-link"
          >
            Analytics &amp; profit
          </Link>
          <Link
            to="/staff/training-report"
            className="hidden md:inline text-xs text-white/80 hover:text-white underline shrink-0"
            data-testid="training-report-link"
          >
            Revenue report
          </Link>
          <Link
            to="/staff/training-fees"
            className="hidden md:inline text-xs text-white/80 hover:text-white underline shrink-0"
            data-testid="training-fees-link"
          >
            Edit fees
          </Link>
          <Link
            to="/staff/training-email-templates"
            className="hidden sm:inline text-xs text-white/80 hover:text-white underline shrink-0"
            data-testid="training-email-templates-link"
          >
            Edit welcome emails
          </Link>
          <Link
            to="/staff/training-email-templates"
            className="sm:hidden text-white/80 hover:text-white p-1.5 rounded-md hover:bg-white/10 shrink-0"
            data-testid="training-email-templates-link-mobile"
            aria-label="Edit welcome emails"
            title="Edit welcome emails"
          >
            <MailIcon className="w-5 h-5" />
          </Link>
          <Button
            variant="outline"
            className="bg-white/10 text-white border-white/40 hover:bg-white/20 hover:text-white font-semibold shrink-0 px-2.5 sm:px-4"
            onClick={() => setImportOpen(true)}
            data-testid="training-course-import-btn"
            aria-label="Import from spreadsheet"
            title="Import from spreadsheet"
          >
            <Upload className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Import</span>
          </Button>
          <Button
            className="bg-white text-[#0F7A45] hover:bg-white/90 font-bold shrink-0 px-2.5 sm:px-4"
            onClick={() => setCreating(true)}
            data-testid="training-course-new-btn"
            aria-label="New course"
          >
            <Plus className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">New Course</span>
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Admin-only "chase overdue balances now" — the same sweep runs on
            the scheduler daily, this button is for when staff wants to fire
            immediately (or verify the setup). */}
        <SmallBalancesCard />
        {['admin', 'manager'].includes(user?.role) && (
          <div className="mb-4 flex items-center justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={runChasesNow}
              disabled={chasing}
              data-testid="run-chases-now-btn"
              className="text-xs"
              title="Immediately fire the T-21/T-19/T-17 outstanding-balance chase sweep"
            >
              <BellRing className={`w-3.5 h-3.5 mr-1 ${chasing ? 'animate-pulse' : ''}`} />
              {chasing ? 'Chasing…' : 'Chase overdue balances'}
            </Button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-20 text-gray-500">Loading…</div>
        ) : courses.length === 0 ? (
          <Card className="p-12 text-center" data-testid="training-courses-empty">
            <GraduationCap className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <h3 className="font-bold text-gray-900 mb-1">No training courses yet</h3>
            <p className="text-gray-500 text-sm mb-4">Create a course, pre-populate the register, then show trainees the QR code on the day.</p>
            <Button className="text-white font-bold" style={{ background: GREEN.primary }} onClick={() => setCreating(true)}>
              <Plus className="w-4 h-4 mr-1.5" /> Create first course
            </Button>
          </Card>
        ) : (
          <div className="space-y-6" data-testid="training-course-groups">
            {groups.map((g) => (
              <section key={g.key} data-testid={`training-course-month-${g.key}`}>
                <div className="flex items-center gap-3 mb-2">
                  <h2 className={`text-base md:text-lg font-bold ${g.past ? 'text-gray-400' : 'text-gray-900'}`} data-testid={`training-course-month-title-${g.key}`}>{g.label}</h2>
                  <span className="text-xs font-semibold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">{g.items.length} course{g.items.length === 1 ? '' : 's'}</span>
                  <div className="flex-1 h-px bg-gray-200" />
                  {g.past && (
                    <button type="button" onClick={() => setShowPast(v => !v)} className="text-xs font-semibold text-gray-500 hover:text-gray-800" data-testid="training-course-toggle-past">
                      {showPast ? 'Hide past courses' : 'Show past courses'}
                    </button>
                  )}
                </div>
                {(!g.past || showPast) && (
                  <div className="space-y-3">
                    {g.items.map((c) => (
              <Card
                    key={c.id}
                    className="p-3 sm:p-4 hover:shadow-md transition cursor-pointer flex flex-wrap items-start sm:items-center gap-3 sm:gap-4"
                    onClick={() => navigate(`/staff/training-courses/${c.id}`)}
                    data-testid={`training-course-row-${c.id}`}
                  >
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: accentForCourse(c).tint, color: accentForCourse(c).dark }}>
                      <GraduationCap className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start sm:items-center flex-wrap gap-x-2 gap-y-1">
                        <div className="font-bold text-gray-900 break-words leading-tight">{c.name}</div>
                        {c.date && (
                          <span
                            className="inline-flex items-center gap-1 text-sm font-bold px-2.5 py-0.5 rounded-lg whitespace-nowrap"
                            style={{ background: accentForCourse(c).primary, color: accentForCourse(c).ink }}
                            data-testid={`training-course-date-badge-${c.id}`}
                          >
                            <CalendarIcon className="w-3.5 h-3.5" /> {fmtCourseDate(c.date)}
                          </span>
                        )}
                        {c.reference && (
                          <span
                            className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded whitespace-nowrap"
                            style={{ background: GREEN.tint, color: GREEN.dark }}
                            data-testid={`training-course-ref-${c.id}`}
                          >
                            {c.reference}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                        <span className="inline-flex items-center gap-1 whitespace-nowrap" data-testid={`training-course-seats-${c.id}`}>
                          <Users className="w-3 h-3" /> {c.registrations_total} booked{c.bookings_total != null && c.bookings_total !== c.registrations_total ? ` (${c.bookings_total} bookings · ${c.registrations_total - c.bookings_total} +1)` : ''}
                        </span>
                        {c.registrations_total > 0 && (
                          <span className="inline-flex items-center gap-1 whitespace-nowrap" data-testid={`training-course-paid-${c.id}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {c.paid_count} paid in full
                            {c.owing_count > 0 && <> · <span className="text-rose-700 font-semibold">{c.owing_count} still owe</span></>}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Grants: {(c.sections || []).map(s => SECTION_LABEL[s] || s).join(', ') || '—'}
                      </div>
                      <SpacesLeftStepper course={c} onSaved={fetchCourses} />
                    </div>
                    <div className="flex items-center gap-2 shrink-0 self-center basis-full sm:basis-auto justify-end sm:justify-start ml-auto">
                      {c.registrations_total > 0 && (() => {
                        const u = collectUrgency(c);
                        return (
                          <div
                            className={`text-right px-3 py-1.5 rounded-lg border ${u.box}`}
                            data-testid={`training-course-owed-${c.id}`}
                            data-urgency={u.key}
                            title={u.title(c)}
                          >
                            <div className={`text-[10px] uppercase tracking-wider font-bold ${u.label}`}>{c.balance_owed_total > 0 ? 'To collect' : 'All paid'}</div>
                            <div className={`text-base sm:text-lg font-bold leading-tight ${u.amount}`}>
                              {c.balance_owed_total > 0 ? `£${Number(c.balance_owed_total).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '£0.00'}
                            </div>
                            {c.balance_owed_total > 0 && u.days != null && (
                              <div className={`text-[10px] font-semibold ${u.label}`} data-testid={`training-course-owed-days-${c.id}`}>{u.daysText}</div>
                            )}
                          </div>
                        );
                      })()}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); setEditCourse(c); }}
                        className="border-gray-300 text-gray-800 hover:bg-gray-50"
                        data-testid={`training-course-edit-btn-${c.id}`}
                        title="Edit name, grants, venue or date"
                      >
                        <Pencil className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Edit</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); setSmsBlastCourse(c); }}
                        className="border-blue-300 text-blue-700 hover:bg-blue-50"
                        data-testid={`training-course-sms-btn-${c.id}`}
                        title="Send an SMS to everyone on this register"
                      >
                        <MessageSquare className="w-4 h-4 sm:mr-1.5" />
                        <span className="hidden sm:inline">SMS text</span>
                      </Button>
                      <Button size="sm" variant="outline">Open</Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); setDeletingCourse(c); }}
                        className="border-red-300 text-red-700 hover:bg-red-50"
                        data-testid={`training-course-delete-btn-${c.id}`}
                        title="Delete this course"
                        aria-label="Delete course"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                        ))}
                  </div>
                )}
              </section>
            ))}
          </div>
        )}
      </div>

      <Dialog open={creating} onOpenChange={(o) => !busy && setCreating(o)}>
        <DialogContent
          data-testid="training-course-create-modal"
          className="!bg-white"
          style={{ background: '#FFFFFF', borderTop: `4px solid ${GREEN.primary}` }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: GREEN.dark }}>New Training Course</DialogTitle>
            <DialogDescription>Give it a name and date, and pick the section(s) attendees will unlock when they confirm attendance.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="tc-preset">Course</Label>
              <Select value={preset} onValueChange={handlePresetChange}>
                <SelectTrigger id="tc-preset" data-testid="training-course-preset" className="bg-white">
                  <SelectValue placeholder="Pick a course…" />
                </SelectTrigger>
                <SelectContent>
                  {COURSE_PRESETS.map(p => (
                    <SelectItem key={p.name} value={p.name} data-testid={`training-course-preset-${p.name.replace(/\s+/g, '-').toLowerCase()}`}>
                      {p.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom" data-testid="training-course-preset-custom">
                    Custom name…
                  </SelectItem>
                </SelectContent>
              </Select>
              {preset === 'custom' && (
                <Input
                  id="tc-name"
                  data-testid="training-course-name"
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Type the course name"
                  autoComplete="off"
                  className="bg-white mt-2"
                />
              )}
            </div>
            <div>
              <Label htmlFor="tc-date">Course date</Label>
              <Input
                id="tc-date"
                data-testid="training-course-date"
                type="date"
                value={form.date}
                onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}
                autoComplete="off"
                className="bg-white"
              />
            </div>
            <div>
              <Label htmlFor="tc-start">Start time <span className="text-gray-400 font-normal">(optional — otherwise the brand default from Email &amp; Message Centre)</span></Label>
              <Input
                id="tc-start"
                data-testid="training-course-start-time"
                type="time"
                value={form.start_time}
                onChange={(e) => setForm(f => ({ ...f, start_time: e.target.value }))}
                className="bg-white"
              />
            </div>
            <div>
              <Label>Sections granted on confirmation</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {SECTIONS.map(s => (
                  <label
                    key={s.value}
                    className="flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer bg-white"
                    style={form.sections.includes(s.value)
                      ? { borderColor: GREEN.primary, background: GREEN.tint }
                      : { borderColor: '#e5e7eb' }}
                  >
                    <Checkbox
                      checked={form.sections.includes(s.value)}
                      onCheckedChange={() => toggleSection(s.value)}
                      data-testid={`training-course-section-${s.value}`}
                    />
                    <span className="text-sm font-medium">{s.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)} disabled={busy}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={busy}
              data-testid="training-course-create-submit"
              className="text-white font-bold"
              style={{ background: GREEN.primary }}
            >
              {busy ? 'Creating…' : 'Create course'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditCourseDialog course={editCourse} open={!!editCourse} onClose={() => setEditCourse(null)} onSaved={fetchCourses} onChangeDate={() => setDateCourse(editCourse)} />
      <ChangeCourseDateDialog course={dateCourse} traineeCount={dateCourse?.registrations_total || 0} open={!!dateCourse} onClose={() => setDateCourse(null)} onChanged={fetchCourses} />
      <CourseSmsBlastModal
        open={!!smsBlastCourse}
        onClose={() => setSmsBlastCourse(null)}
        courseId={smsBlastCourse?.id}
        courseSummary={smsBlastCourse}
      />

      <SpreadsheetImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={(course) => {
          fetchCourses();
          // Take the user straight to the freshly-imported course so they
          // can eyeball the register before doing anything else.
          if (course?.id) navigate(`/staff/training-courses/${course.id}`);
        }}
      />

      <Dialog
        open={!!deletingCourse}
        onOpenChange={(o) => { if (!o && !deleteBusy) setDeletingCourse(null); }}
      >
        <DialogContent
          data-testid="training-course-delete-modal"
          className="!bg-white"
          style={{ background: '#FFFFFF', borderTop: `4px solid #DC2626` }}
        >
          <DialogHeader>
            <DialogTitle className="text-red-700">
              {deletingCourse?.registrations_total > 0 ? 'Register not empty' : 'Delete this course?'}
            </DialogTitle>
            <DialogDescription>
              {deletingCourse?.registrations_total > 0 ? (
                <>
                  <strong>{deletingCourse.name}</strong> still has <strong>{deletingCourse.registrations_total}</strong>{' '}
                  trainee{deletingCourse.registrations_total === 1 ? '' : 's'} on the register.
                  Open the course and use the bulk-select bar to <strong>move or delete</strong> everyone first, then come back and delete the course.
                </>
              ) : (
                <>This permanently removes <strong>{deletingCourse?.name || 'the course'}</strong>. This can&apos;t be undone.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeletingCourse(null)}
              disabled={deleteBusy}
              data-testid="training-course-delete-cancel"
            >
              {deletingCourse?.registrations_total > 0 ? 'Close' : 'Cancel'}
            </Button>
            {deletingCourse?.registrations_total > 0 ? (
              <Button
                onClick={() => {
                  const id = deletingCourse.id;
                  setDeletingCourse(null);
                  navigate(`/staff/training-courses/${id}`);
                }}
                className="text-white font-semibold"
                style={{ background: GREEN.primary }}
                data-testid="training-course-delete-open-instead"
              >
                Open course
              </Button>
            ) : (
              <Button
                onClick={async () => {
                  if (!deletingCourse) return;
                  setDeleteBusy(true);
                  try {
                    const r = await fetch(
                      `${API_URL}/api/staff/training-courses/${deletingCourse.id}`,
                      { method: 'DELETE', credentials: 'include' },
                    );
                    if (!r.ok) {
                      const err = await r.json().catch(() => ({}));
                      throw new Error(err.detail || `HTTP ${r.status}`);
                    }
                    toast.success(`Deleted "${deletingCourse.name}"`);
                    setDeletingCourse(null);
                    fetchCourses();
                  } catch (e) {
                    toast.error('Delete failed', { description: String(e.message || e).slice(0, 200) });
                  } finally {
                    setDeleteBusy(false);
                  }
                }}
                disabled={deleteBusy}
                className="bg-red-600 hover:bg-red-500 text-white"
                data-testid="training-course-delete-confirm"
              >
                {deleteBusy ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Deleting…</>
                ) : (
                  <><Trash2 className="w-4 h-4 mr-1.5" />Delete course</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
